import { openDB, IDBPDatabase, DBSchema } from 'idb';
import { INDEXED_DB } from '../constants/global.constants';

// Define your database schema
interface MyDB extends DBSchema {
  eventStore: {
    key: number;
    value: any /* Your data type */;
    indexes: { userNumberIndex: string };
  };
}

const primaryKey = 'id';
const docKey = 'userNumber';
const docIndex = 'userNumberIndex';
const eventStore = 'eventStore';

class IndexedDBService {
  private dbPromise: Promise<IDBPDatabase<MyDB>>;

  constructor(dbName: string, version: number) {
    this.dbPromise = openDB<MyDB>(dbName, version, {
      upgrade(db) {
        const store = db.createObjectStore(eventStore, {
          keyPath: primaryKey,
          autoIncrement: true,
        });
        store.createIndex(docIndex, docKey, {
          unique: false,
        });
      },
    });
  }

  async getAllEvents() {
    const db = await this.dbPromise;
    const events = await db.getAll(eventStore);
    return events.sort((a, b) => a.timestamp - b.timestamp);
  }

  async getEventsByUserNumber(userNumber: string) {
    if (!userNumber) return await [];
    try {
      const db = await this.dbPromise;
      const index = db.transaction(eventStore).store.index(docIndex);
      const events = await index.getAll(userNumber);
      return events.sort((a, b) => a.timestamp - b.timestamp);
    } catch (error) {
      throw new Error(JSON.stringify(error));
    }
  }

  async addEvents(events: any[]) {
    const db = await this.dbPromise;
    const tx = db.transaction(eventStore, 'readwrite');
    const store = tx.objectStore(eventStore);
    await Promise.all(events.map((event) => store.add(event)));
    await tx.done;
  }

  async addEvent(event: any, userNumber: string, timestamp: number) {
    // const timestamp = event.timestamp || new Date().getTime();
    const db = await this.dbPromise;
    const tx = db.transaction(eventStore, 'readwrite');
    const store = tx.objectStore(eventStore);
    try {
      await store.add({ ...event, userNumber, timestamp });
      await tx.done;
    } catch (error) {
      console.error('Error adding data to IndexedDB:', error);
    }
  }

  async getEventById(id: number) {
    const db = await this.dbPromise;
    const tx = db.transaction(eventStore, 'readonly');
    const store = tx.objectStore(eventStore);

    try {
      const event = await store.get(id);
      await tx.done;
      return event;
    } catch (error) {
      console.error('Error fetching event with ID:', id, error);
      throw error;
    }
  }

  async updateEvent(id: string, updatedEvent: any) {
    const db = await this.dbPromise;
    const tx = db.transaction(eventStore, 'readwrite');
    const store = tx.objectStore(eventStore);

    // Get the item with the specified ID
    const request = await store.index(docIndex).get(id);

    request.onsuccess = function (event: any) {
      const result = event.target.result;
      if (result) {
        // If the item is found, update it
        store.put({ ...result, ...updatedEvent });
      } else {
        console.error('Item not found with ID:', id);
      }
    };

    request.onerror = function (event: any) {
      console.error('Error finding item:', event.target.error);
    };

    await tx.done;
  }

  async deleteEvent(id: number) {
    const db = await this.dbPromise;
    const tx = db.transaction(eventStore, 'readwrite');
    const store = tx.objectStore(eventStore);
    // Attempt to delete the item with the specified ID
    try {
      await store.delete(id);
      await tx.done;
    } catch (error) {
      console.error('Error deleting event with ID:', id, error);
    }
  }

  async purgeEventsExceptForUser(userNumber: string) {
    const db = await this.dbPromise;
    const tx = db.transaction([eventStore], 'readwrite');
    const store = tx.objectStore(eventStore);
    const allEvents = await store.getAll();

    allEvents.forEach(async (event: any) => {
      if (event.userNumber !== userNumber) {
        await store.delete(event.id);
      }
    });
  }

  // Add more methods as needed
}

const indexedDBService = new IndexedDBService(
  INDEXED_DB.NAME,
  INDEXED_DB.VERSION,
);

// export default IndexedDBService;
export default indexedDBService;
